/*
 * WithPaymentProvider.tsx (AbstractECommerce)
 *
 * Copyright © 2020 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Martin Witczak, 2020
 *
 * @file WithPaymentProvider.tsx
 * @author Martin Witczak
 * @copyright 2020 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BraintreeWebDropIn, { Dropin } from 'braintree-web-drop-in';
import { getToken } from '../../../Store/Transactions';
import { sendFrontendError } from '../../../Services/FrontendError';
import { customAlphabet } from 'nanoid';
import { fetchSettings } from '../../../Store/ShopSettings';
import { LocalStorage } from '@abstract/abstractwebcommon-client/utils/sharedLocalStorage';
import { ThemeMode } from '@abstract/abstractwebcommon-shared/enum/theme';
import { PaymentErrorCode } from './paymentErrors';

// Map our i18n short codes to the locales Braintree drop-in accepts.
// Anything unmapped falls back to en_US (drop-in's default).
const BRAINTREE_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: 'en_US',
  de: 'de_DE',
  ja: 'ja_JP',
  zh: 'zh_CN',
  ko: 'ko_KR'
};

const resolveBraintreeLocale = (language: string | undefined): string => {
  if (!language) return 'en_US';
  const primary = language.split('-')[0].toLowerCase();
  return BRAINTREE_LOCALE_BY_LANGUAGE[primary] ?? 'en_US';
};

export const withPaymentProvider = (component) => (props) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const braintreeLocale = resolveBraintreeLocale(i18n.language);
  const token = useSelector((state) => state.transactions.token);
  const sharedSubscriptions = useSelector(
    (state) => state.sharedSubscriptions
  ); /**< Shared subscription state */
  const userIPAddress = useSelector(
    (state) => state.transactions.userIPAddress
  );
  const paymentWrapperRef = useRef<HTMLElement | undefined>(undefined);
  // Mirrors the wrapper DOM node into state via a callback ref. A bare ref has a
  // stable identity, so listing it in an effect's dependency array never
  // re-triggers the effect when the node finally mounts — which previously left
  // the drop-in uncreated whenever `token` resolved (or was already cached in
  // Redux from a prior product) before the wrapper div was in the DOM. Driving
  // the mount effect off this state makes creation order-independent.
  const [paymentWrapperNode, setPaymentWrapperNode] = useState<
    HTMLElement | null
  >(null);
  const setPaymentWrapperRef = useCallback((node: HTMLElement | null) => {
    paymentWrapperRef.current = node ?? undefined;
    setPaymentWrapperNode(node ?? null);
  }, []);
  const [widgetInstance, setWidgetInstance] = useState<Dropin>(null);
  // Ref mirrors widgetInstance so async teardown/create paths always see the
  // latest value without stale closures, and so we can no-op a second teardown
  // call on the same instance.
  const widgetInstanceRef = useRef<Dropin | null>(null);
  const widgetCreationPromiseRef = useRef<Promise<void> | null>(null);

  const [paymentWidgetReady, setPaymentWidgetReady] = useState(false);
  const [frontendPaymentError, setFrontendPaymentError] = useState(null);

  const createPaymentWidgetPromise = () => {
    return new Promise((resolve, reject) => {
      BraintreeWebDropIn.create(
        {
          authorization: token,
          selector: paymentWrapperRef.current,
          locale: braintreeLocale,
          card: {
            cardholderName: {
              required: true
            },
            overrides: {
              fields: {
                cvv: true,
                expirationDate: {
                  prefill:
                    sharedSubscriptions?.subscriptionPaymentMethod
                      ?.expirationDate
                },
                number: {
                  placeholder:
                    sharedSubscriptions?.subscriptionPaymentMethod
                      ?.maskedNumber ?? '•••• •••• •••• ••••'
                }
              },
              styles: {
                input: {
                  color:
                    LocalStorage.getThemeMode() === ThemeMode.darkMode
                      ? '#CCC'
                      : '#000000'
                },
                'input:focus': {
                  color:
                    LocalStorage.getThemeMode() === ThemeMode.darkMode
                      ? '#CCC'
                      : '#000000'
                }
              }
            }
          },
          paypal: {
            flow: 'vault',
            currency: 'USD'
          },
          threeDSecure: true,
          // Collects device data for Braintree's fraud-detection signal and
          // exposes it as `payload.deviceData` on requestPaymentMethod. Without
          // this, the server's deviceData arrives undefined.
          dataCollector: true
        },
        (err, widgetInstance) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(widgetInstance);
        }
      );
    });
  };

  const destroyWidget = async () => {
    const current = widgetInstanceRef.current;
    if (!current) return;
    widgetInstanceRef.current = null;
    setWidgetInstance(null);
    setPaymentWidgetReady(false);
    if (typeof current.teardown === 'function') {
      try {
        await current.teardown();
      } catch (e) {
        console.error('Failed to teardown Braintree drop-in', e);
      }
    }
  };

  // Serializes create/destroy so two callers can't race and end up with two
  // drop-in instances attached to the same DOM element (which Braintree then
  // refuses, silently leaving a stale widget whose cached payload can skip 3DS
  // on the next requestPaymentMethod call).
  const createWidget = async () => {
    if (!token || !paymentWrapperRef.current) return;
    const previous = widgetCreationPromiseRef.current ?? Promise.resolve();
    const next = previous.then(async () => {
      await destroyWidget();
      try {
        const newWidget = (await createPaymentWidgetPromise()) as Dropin;
        widgetInstanceRef.current = newWidget;
        setWidgetInstance(newWidget);
        setPaymentWidgetReady(true);
      } catch (e) {
        widgetInstanceRef.current = null;
        setWidgetInstance(null);
        setPaymentWidgetReady(false);
        console.error('Failed to create Braintree drop-in', e);
        sendFrontendError('braintree-dropin-create', e);
      }
    });
    widgetCreationPromiseRef.current = next;
    return next;
  };

  useEffect(() => {
    dispatch(fetchSettings());
  }, []);

  useEffect(() => {
    dispatch(getToken());
  }, [getToken]);

  useEffect(() => {
    if (paymentWrapperNode == null || token == null) return () => {};

    createWidget();

    return () => {
      destroyWidget();
    };
  }, [token, paymentWrapperNode, braintreeLocale]);

  const checkPaymentMethod = (price, customerDetails) => {
    setFrontendPaymentError(null);
    const currentWidget = widgetInstanceRef.current;
    return new Promise((resolve, reject) => {
      if (!currentWidget) {
        reject(new Error('Payment widget is not ready.'));
        return;
      }
      const trim50 = (value?: string) => (value || '').substring(0, 50);
      // Drop-in already collects device data via the top-level `dataCollector`
      // option above; per Braintree docs the 3DS parameters object accepts an
      // `additionalInformation` block and the recommended fields below.
      // Always send the shipping block (even when it equals billing): per
      // Braintree's 3DS docs, providing shipping signals strengthens the
      // issuer's frictionless-auth decision regardless of whether the
      // addresses match.
      const additionalInformation: Record<string, unknown> = {
        ipAddress: userIPAddress,
        shippingGivenName: trim50(customerDetails.firstName),
        shippingSurname: trim50(customerDetails.lastName),
        shippingAddress: {
          streetAddress: trim50(customerDetails.address1),
          extendedAddress: trim50(customerDetails.address2),
          locality: trim50(customerDetails.city),
          region: trim50(customerDetails.stateOrProvince),
          postalCode: trim50(customerDetails.zipcode),
          countryCodeAlpha2: customerDetails.country
        }
      };
      const threeDSecureDetails = {
        additionalInformation,
        amount: price,
        email: customerDetails.email,
        billingAddress: {
          givenName: trim50(customerDetails.billingFirstName),
          surname: trim50(customerDetails.billingLastName),
          streetAddress: trim50(customerDetails.billingAddress1),
          extendedAddress: trim50(customerDetails.billingAddress2),
          locality: trim50(customerDetails.billingCity),
          region: trim50(customerDetails.billingStateOrProvince),
          postalCode: trim50(customerDetails.billingZipcode),
          countryCodeAlpha2: customerDetails.billingCountry
        }
      };

      currentWidget.requestPaymentMethod(
        {
          threeDSecure: threeDSecureDetails
        },
        (err, payload) => {
          if (err) {
            if (err.name !== 'BraintreeError') {
              reject(err);
            } else {
              const nanoid = customAlphabet('1234567890abcdef', 8);
              const errorId = nanoid();
              setFrontendPaymentError({ error: err, errorId });
              console.log('Payment error', err, errorId);
              err.threeDSecureDetails = threeDSecureDetails;
              sendFrontendError(errorId, err).finally(() => reject(err));
            }
            return;
          }
          // Once we have the nonce, immediately clear drop-in's cached payment
          // method. Drop-in transitions to PaymentMethodsView after a successful
          // tokenize+3DS and on the next requestPaymentMethod call returns the
          // cached payload with liabilityShifted already set, which causes
          // _shouldPerformThreeDSecureVerification to skip verifyCard. Clearing
          // here forces fresh tokenization (and fresh 3DS) on any retry,
          // regardless of which error path the failure takes.
          try {
            if (typeof currentWidget.clearSelectedPaymentMethod === 'function') {
              currentWidget.clearSelectedPaymentMethod();
            }
          } catch (clearError) {
            console.error(
              'Failed to clear Braintree drop-in selected payment method',
              clearError
            );
          }
          // Surface a 3DS authentication failure here instead of letting the
          // server reject the transaction with a generic error. Only reject
          // when the issuer attempted 3DS and the user failed — when
          // liabilityShiftPossible is false, the card simply doesn't
          // participate in 3DS, which Braintree's transaction-level
          // `threeDSecure.required: true` still accepts.
          const info =
            payload && payload.type === 'CreditCard'
              ? payload.threeDSecureInfo
              : null;
          if (info && info.liabilityShiftPossible && !info.liabilityShifted) {
            const liabilityError: any = new Error(
              '3D Secure authentication did not shift liability.'
            );
            liabilityError.name = 'BraintreeError';
            liabilityError.code = PaymentErrorCode.LIABILITY_NOT_SHIFTED;
            setFrontendPaymentError({
              code: PaymentErrorCode.LIABILITY_NOT_SHIFTED,
              error: liabilityError
            });
            reject(liabilityError);
            return;
          }
          resolve(payload);
        }
      );
    });
  };

  return component({
    paymentWrapperRef,
    setPaymentWrapperRef,
    paymentWidgetReady,
    widgetInstance,
    createWidget,
    destroyWidget,
    checkPaymentMethod,
    frontendPaymentError,
    ...props
  });
};
